import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import dayjs from 'dayjs';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { DriverLoyaltyDetailDashboardDto } from 'api/dashboard/dtos/DriverLoyaltyDetailDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import useInfiniteReveal from '../components/useInfiniteReveal';
import upcomingStyles from '../upcomingEvents/UpcomingEvents.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './DriverLoyaltyTable.module.scss';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;
const LOYALTY_THRESHOLD = 70;
const RESERVATIONS_FILTER_KEY = 'RESERVATIONSFILTER';

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const navigateToDriverReservations = (
	driverId: string,
	driverName: string,
	dateFrom: Date | null | undefined,
	dateTo: Date | null | undefined,
) => {
	const payload: Record<string, unknown> = { driverId, driverName };
	if (dateFrom) payload.reservationStartDate = dateFrom;
	if (dateTo) payload.reservationEndDate = dateTo;
	try {
		const existing = localStorage.getItem(RESERVATIONS_FILTER_KEY);
		const current = existing ? JSON.parse(existing) : {};
		localStorage.setItem(RESERVATIONS_FILTER_KEY, JSON.stringify({ ...current, ...payload }));
	} catch {
		localStorage.setItem(RESERVATIONS_FILTER_KEY, JSON.stringify(payload));
	}
	window.open('/reservations?tab=reservations', '_blank');
};

const DriverLoyaltyTable = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<DriverLoyaltyDetailDashboardDto[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [search, setSearch] = useState('');
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('driverLoyaltyTable');

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo };
				const result = await DashboardService.getDriverLoyaltyDetails(criteria);
				if (!cancelled) {
					setData(result);
				}
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo]);

	const filtered = useMemo(() => {
		if (!data) return [];
		const q = search.trim().toLowerCase();
		if (!q) return data;
		return data.filter(d =>
			(d.driverName && d.driverName.toLowerCase().includes(q)) ||
			(d.favoriteRegistrationNumber && d.favoriteRegistrationNumber.toLowerCase().includes(q))
		);
	}, [data, search]);

	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(filtered, PAGE_SIZE);

	const buildVehicleTooltip = (item: DriverLoyaltyDetailDashboardDto) =>
		[item.favoriteBrand, item.favoriteModel].filter(Boolean).join(' ') || undefined;

	const renderOptions = (
		<div className={styles.tableOptions}>
			<input
				type="text"
				className={styles.searchInput}
				placeholder={t('dashboard.driver_loyalty_table.search_placeholder' as any) as string}
				value={search}
				onChange={e => setSearch(e.target.value)}
			/>
			<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />
		</div>
	);

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.driver_loyalty_table.title' as any) as string}
			subTitleTooltip={t('dashboard.driver_loyalty_table.tooltip' as any) as string}
			renderOptions={renderOptions}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}><LoadingSpinner /></div>
			) : filtered.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<div className={upcomingStyles.tableWrapper}>
						<table className={upcomingStyles.table}>
							<thead>
								<tr>
									<th>{t('dashboard.driver_loyalty_table.col_driver' as any)}</th>
									<th>{t('dashboard.driver_loyalty_table.col_favorite' as any)}</th>
									<th className={styles.numericHeader}>{t('dashboard.driver_loyalty_table.col_loyalty' as any)}</th>
									<th className={styles.numericHeader}>{t('dashboard.driver_loyalty_table.col_reservations' as any)}</th>
									<th className={styles.numericHeader}>{t('dashboard.driver_loyalty_table.col_distinct' as any)}</th>
									<th>{t('dashboard.driver_loyalty_table.col_last' as any)}</th>
								</tr>
							</thead>
							<tbody>
								{visibleItems.map(item => {
									const pct = Number(item.loyaltyPercent);
									const isLoyal = pct >= LOYALTY_THRESHOLD;
									return (
										<tr
											key={item.driverId}
											className={upcomingStyles.tableRow}
											onClick={() => navigateToDriverReservations(
													item.driverId,
													item.driverName,
													props.filters?.dateFrom ?? dateFrom,
													props.filters?.dateTo ?? dateTo,
												)}
										>
											<td><span className={styles.driverName}>{item.driverName}</span></td>
											<td>{item.favoriteRegistrationNumber
												? <span className={styles.plate}><span data-tip={buildVehicleTooltip(item)} data-for="driver-loyalty-vehicle-tooltip" data-place="right">{item.favoriteRegistrationNumber}</span></span>
												: <span className={upcomingStyles.muted}>—</span>}</td>
											<td className={styles.numericCell}>
												<span className={`${styles.loyaltyBadge} ${isLoyal ? styles.loyaltyHigh : styles.loyaltyLow}`}>
													{pct.toFixed(0)}%
												</span>
											</td>
											<td className={styles.numericCell}>{item.totalReservations}</td>
											<td className={styles.numericCell}>{item.distinctVehicles}</td>
											<td>{item.lastReservationDate ? dayjs(item.lastReservationDate).format('DD/MM/YYYY') : <span className={upcomingStyles.muted}>—</span>}</td>
										</tr>
									);
								})}
								{hasMore && (
									<tr ref={sentinelRef as unknown as React.Ref<HTMLTableRowElement>}>
										<td colSpan={6} className={infiniteStyles.sentinelRow} />
									</tr>
								)}
							</tbody>
						</table>
					</div>
					<ReactTooltip id="driver-loyalty-vehicle-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default DriverLoyaltyTable;
